import moment, { Moment } from 'moment';

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function resolveRelativeDateKeyword(dateValue: string): string | null {
  const normalized = dateValue
    .trim()
    .toLowerCase()
    .replace(/[.!?,;:]+$/g, '');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (normalized === 'today') {
    return formatLocalDate(today);
  }

  if (normalized === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return formatLocalDate(tomorrow);
  }

  if (normalized === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return formatLocalDate(yesterday);
  }

  return null;
}

export function parseLocalDateString(dateValue: string | Date | Moment): string {
  if (!dateValue) return '';

  try {
    if (moment.isMoment(dateValue)) {
      return dateValue.format('YYYY-MM-DD');
    }

    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = (dateValue.getMonth() + 1).toString().padStart(2, '0');
      const day = dateValue.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const dateStr = String(dateValue);
    const relativeDate = resolveRelativeDateKeyword(dateStr);
    if (relativeDate) {
      return relativeDate;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }

    // extract year, month, day to avoid timezone issues
    const match = dateStr.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
    if (match) {
      const year = match[1];
      const month = match[2].padStart(2, '0');
      const day = match[3].padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return formatLocalDate(date);
    }

    return '';
  } catch (e) {
    console.error('Error parsing date:', e);
    return '';
  }
}

export function isValidDateForDiscrepancy(dateStr: string): boolean {
  if (!dateStr) return false;

  // Check for year before 1910
  const yearMatch = dateStr.match(/^(\d{4})/);
  if (yearMatch) {
    const year = parseInt(yearMatch[1], 10);
    if (year < 1910) return false;
  }

  return true;
}

export function createLocalDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  // Parse YYYY-MM-DD format
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
    const day = parseInt(match[3], 10);
    return new Date(year, month, day);
  }

  return null;
}

export function formatLocalDateTimeWithOffset(date: Date): string {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (value: number) => String(value).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = pad(Math.floor(absOffset / 60));
  const offsetMins = pad(absOffset % 60);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMins}`;
}

export function toLocalDateTimeString(value: unknown): string {
  if (!value) {
    return '';
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return '';
    }

    const isUtcMidnight =
      value.getUTCHours() === 0 &&
      value.getUTCMinutes() === 0 &&
      value.getUTCSeconds() === 0 &&
      value.getUTCMilliseconds() === 0;
    const isLocalMidnight =
      value.getHours() === 0 &&
      value.getMinutes() === 0 &&
      value.getSeconds() === 0 &&
      value.getMilliseconds() === 0;

    if (isUtcMidnight && !isLocalMidnight) {
      const normalizedLocalDate = new Date(
        value.getUTCFullYear(),
        value.getUTCMonth(),
        value.getUTCDate(),
        0,
        0,
        0,
        0,
      );
      return formatLocalDateTimeWithOffset(normalizedLocalDate);
    }

    return formatLocalDateTimeWithOffset(value);
  }

  const text = String(value).trim();
  if (!text) {
    return '';
  }

  if (text.includes('T')) {
    return text;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) {
    return text;
  }

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  return formatLocalDateTimeWithOffset(new Date(year, month, day, 0, 0, 0, 0));
}
